import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Calculator,
  TrendingUp,
  TrendingDown,
  Wallet,
  FileText,
  Building2,
  Plus,
  Search,
  Loader2,
  Pencil,
  Trash2,
  MoreVertical
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';

// Import separated tab components
import { ProfitLossTab, BalanceSheetTab, TrialBalanceTab, CashBookTab, BankBookTab } from '@/components/accounting';
import AssetsLiabilitiesTab from '@/components/AssetsLiabilitiesTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AccountingPage = () => {
  const { getAuthHeader } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  
  // Date filters
  const today = new Date().toISOString().split('T')[0];
  const firstDayOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0];
  const [fromDate, setFromDate] = useState(firstDayOfMonth);
  const [toDate, setToDate] = useState(today);

  // Account Dialog
  const [showAccountDialog, setShowAccountDialog] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [accountForm, setAccountForm] = useState({
    account_code: '',
    account_name: '',
    account_type: 'asset',
    account_group: 'fixed_assets',
    opening_balance: 0,
    description: ''
  });

  // Account types and groups
  const accountTypes = [
    { value: 'asset', label: 'Asset' },
    { value: 'liability', label: 'Liability' },
    { value: 'equity', label: 'Equity' },
    { value: 'income', label: 'Income' },
    { value: 'expense', label: 'Expense' }
  ];

  const accountGroups = {
    asset: [
      { value: 'cash_in_hand', label: 'Cash in Hand' },
      { value: 'bank_accounts', label: 'Bank Accounts' },
      { value: 'fixed_assets', label: 'Fixed Assets' },
      { value: 'receivables', label: 'Receivables' }
    ],
    liability: [
      { value: 'payables', label: 'Payables' },
      { value: 'loans', label: 'Loans' },
      { value: 'provisions', label: 'Provisions' }
    ],
    equity: [
      { value: 'capital', label: 'Capital' },
      { value: 'reserves', label: 'Reserves' }
    ],
    income: [
      { value: 'fee_income', label: 'Fee Income' },
      { value: 'other_income', label: 'Other Income' }
    ],
    expense: [
      { value: 'salary_expense', label: 'Salary Expense' },
      { value: 'operating_expense', label: 'Operating Expense' },
      { value: 'admin_expense', label: 'Admin Expense' }
    ]
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/accounting/accounts`, {
        headers: getAuthHeader()
      });
      setAccounts(res.data);
    } catch (error) {
      toast.error('Failed to fetch accounts');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const getAccountTypeColor = (type) => {
    switch (type) {
      case 'asset': return 'bg-blue-100 text-blue-800';
      case 'liability': return 'bg-red-100 text-red-800';
      case 'equity': return 'bg-purple-100 text-purple-800';
      case 'income': return 'bg-green-100 text-green-800';
      case 'expense': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Filter accounts
  const filteredAccounts = accounts.filter(acc => {
    const matchesSearch = acc.account_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      acc.account_code.includes(searchTerm);
    const matchesType = filterType === 'all' || acc.account_type === filterType;
    return matchesSearch && matchesType;
  });

  // Handle Account CRUD
  const handleAddAccount = () => {
    setEditingAccount(null);
    setAccountForm({
      account_code: '',
      account_name: '',
      account_type: 'asset',
      account_group: 'fixed_assets',
      opening_balance: 0,
      description: ''
    });
    setShowAccountDialog(true);
  };

  const handleEditAccount = (account) => {
    setEditingAccount(account);
    setAccountForm({
      account_code: account.account_code,
      account_name: account.account_name,
      account_type: account.account_type,
      account_group: account.account_group,
      opening_balance: account.opening_balance || 0,
      description: account.description || ''
    });
    setShowAccountDialog(true);
  };

  const handleDeleteAccount = async (account) => {
    if (!window.confirm(`Are you sure you want to delete "${account.account_name}"? This cannot be undone.`)) {
      return;
    }
    try {
      await axios.delete(`${API_URL}/api/accounting/accounts/${account.id}`, {
        headers: getAuthHeader()
      });
      toast.success('Account deleted successfully');
      fetchAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete account');
    }
  };

  const handleSaveAccount = async () => {
    if (!accountForm.account_code || !accountForm.account_name) {
      toast.error('Account code and name are required');
      return;
    }

    setLoading(true);
    try {
      if (editingAccount) {
        await axios.put(`${API_URL}/api/accounting/accounts/${editingAccount.id}`, accountForm, {
          headers: getAuthHeader()
        });
        toast.success('Account updated successfully');
      } else {
        await axios.post(`${API_URL}/api/accounting/accounts`, accountForm, {
          headers: getAuthHeader()
        });
        toast.success('Account created successfully');
      }
      setShowAccountDialog(false);
      fetchAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save account');
    } finally {
      setLoading(false);
    }
  };

  // Calculate summary
  const totalAssets = accounts.filter(a => a.account_type === 'asset').reduce((sum, a) => sum + (a.current_balance || 0), 0);
  const totalLiabilities = accounts.filter(a => a.account_type === 'liability').reduce((sum, a) => sum + (a.current_balance || 0), 0);
  const totalIncome = accounts.filter(a => a.account_type === 'income').reduce((sum, a) => sum + (a.current_balance || 0), 0);
  const totalExpenses = accounts.filter(a => a.account_type === 'expense').reduce((sum, a) => sum + (a.current_balance || 0), 0);

  return (
    <DashboardLayout title="Accounting">
      <div className="space-y-6">
        {/* Date Range Filter */}
        <div className="flex items-center gap-4 justify-end">
          <div className="flex items-center gap-2">
            <Label>From:</Label>
            <Input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-40"
            />
          </div>
          <div className="flex items-center gap-2">
            <Label>To:</Label>
            <Input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-40"
            />
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-4 gap-4">
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Assets</p>
                  <p className="text-2xl font-bold">{formatCurrency(totalAssets)}</p>
                </div>
                <Wallet className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-red-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Liabilities</p>
                  <p className="text-2xl font-bold">{formatCurrency(totalLiabilities)}</p>
                </div>
                <FileText className="h-8 w-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Income</p>
                  <p className="text-2xl font-bold">{formatCurrency(totalIncome)}</p>
                </div>
                <TrendingUp className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-orange-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Expenses</p>
                  <p className="text-2xl font-bold">{formatCurrency(totalExpenses)}</p>
                </div>
                <TrendingDown className="h-8 w-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Tabs */}
        <Tabs defaultValue="accounts" className="space-y-4">
          <TabsList className="grid w-full grid-cols-8">
            <TabsTrigger value="accounts" className="flex items-center gap-1">
              <Calculator className="h-4 w-4" /> Accounts
            </TabsTrigger>
            <TabsTrigger value="banks" className="flex items-center gap-1">
              <Building2 className="h-4 w-4" /> Banks
            </TabsTrigger>
            <TabsTrigger value="assets-liabilities">Assets/Liab</TabsTrigger>
            <TabsTrigger value="cash-book">Cash Book</TabsTrigger>
            <TabsTrigger value="bank-book">Bank Book</TabsTrigger>
            <TabsTrigger value="profit-loss">P&L</TabsTrigger>
            <TabsTrigger value="balance-sheet">Balance Sheet</TabsTrigger>
            <TabsTrigger value="trial-balance">Trial Balance</TabsTrigger>
          </TabsList>

          {/* Chart of Accounts Tab */}
          <TabsContent value="accounts">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Chart of Accounts</CardTitle>
                  <CardDescription>Manage all accounts in your ledger</CardDescription>
                </div>
                <Button onClick={handleAddAccount} data-testid="add-account-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Account
                </Button>
              </CardHeader>
              <CardContent>
                {/* Search and Filter */}
                <div className="flex gap-4 mb-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search accounts..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <Select value={filterType} onValueChange={setFilterType}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      {accountTypes.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {loading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin" />
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Code</TableHead>
                        <TableHead>Account Name</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Group</TableHead>
                        <TableHead className="text-right">Balance</TableHead>
                        <TableHead className="w-[50px]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredAccounts.map((account) => (
                        <TableRow key={account.id}>
                          <TableCell className="font-mono">{account.account_code}</TableCell>
                          <TableCell className="font-medium">{account.account_name}</TableCell>
                          <TableCell>
                            <Badge className={getAccountTypeColor(account.account_type)}>
                              {account.account_type}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground capitalize">
                            {account.account_group?.replace(/_/g, ' ')}
                          </TableCell>
                          <TableCell className={`text-right font-medium ${
                            account.current_balance >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {formatCurrency(account.current_balance)}
                          </TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" data-testid={`account-actions-${account.account_code}`}>
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleEditAccount(account)}>
                                  <Pencil className="h-4 w-4 mr-2" /> Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => handleDeleteAccount(account)}
                                  className="text-red-600"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" /> Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                      {filteredAccounts.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                            No accounts found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                )}

                <div className="mt-4 text-sm text-muted-foreground">
                  Showing {filteredAccounts.length} of {accounts.length} accounts
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Banks Tab */}
          <TabsContent value="banks">
            <BankAccountsTab getAuthHeader={getAuthHeader} />
          </TabsContent>

          {/* Assets & Liabilities Tab */}
          <TabsContent value="assets-liabilities">
            <AssetsLiabilitiesTab getAuthHeader={getAuthHeader} />
          </TabsContent>

          {/* Cash Book Tab */}
          <TabsContent value="cash-book">
            <CashBookTab 
              getAuthHeader={getAuthHeader} 
              fromDate={fromDate} 
              toDate={toDate}
              accounts={accounts}
            />
          </TabsContent>

          {/* Bank Book Tab */}
          <TabsContent value="bank-book">
            <BankBookTab 
              getAuthHeader={getAuthHeader} 
              fromDate={fromDate} 
              toDate={toDate}
              accounts={accounts}
            />
          </TabsContent>

          {/* Profit & Loss Tab */}
          <TabsContent value="profit-loss">
            <ProfitLossTab 
              getAuthHeader={getAuthHeader} 
              fromDate={fromDate} 
              toDate={toDate}
            />
          </TabsContent>

          {/* Balance Sheet Tab */}
          <TabsContent value="balance-sheet">
            <BalanceSheetTab 
              getAuthHeader={getAuthHeader} 
              asOfDate={toDate}
            />
          </TabsContent>

          {/* Trial Balance Tab */}
          <TabsContent value="trial-balance">
            <TrialBalanceTab 
              getAuthHeader={getAuthHeader} 
              asOfDate={toDate}
            />
          </TabsContent>
        </Tabs>

        {/* Account Add/Edit Dialog */}
        <Dialog open={showAccountDialog} onOpenChange={setShowAccountDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {editingAccount ? 'Edit Account' : 'Add New Account'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Account Code *</Label>
                  <Input
                    value={accountForm.account_code}
                    onChange={(e) => setAccountForm({ ...accountForm, account_code: e.target.value })}
                    placeholder="e.g., 5900"
                    data-testid="account-code-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Account Type *</Label>
                  <Select
                    value={accountForm.account_type}
                    onValueChange={(v) => setAccountForm({ 
                      ...accountForm, 
                      account_type: v,
                      account_group: accountGroups[v]?.[0]?.value || ''
                    })}
                  >
                    <SelectTrigger data-testid="account-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {accountTypes.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Account Name *</Label>
                <Input
                  value={accountForm.account_name}
                  onChange={(e) => setAccountForm({ ...accountForm, account_name: e.target.value })}
                  placeholder="e.g., Sports Equipment Expense"
                  data-testid="account-name-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Account Group *</Label>
                  <Select
                    value={accountForm.account_group}
                    onValueChange={(v) => setAccountForm({ ...accountForm, account_group: v })}
                  >
                    <SelectTrigger data-testid="account-group-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {accountGroups[accountForm.account_type]?.map(g => (
                        <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Opening Balance</Label>
                  <Input
                    type="number"
                    value={accountForm.opening_balance}
                    onChange={(e) => setAccountForm({ ...accountForm, opening_balance: parseFloat(e.target.value) || 0 })}
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Description (optional)</Label>
                <Input
                  value={accountForm.description}
                  onChange={(e) => setAccountForm({ ...accountForm, description: e.target.value })}
                  placeholder="Brief description of this account"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAccountDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSaveAccount} 
                disabled={loading || !accountForm.account_code || !accountForm.account_name}
                data-testid="save-account-btn"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {editingAccount ? 'Update Account' : 'Create Account'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

// Bank Accounts Tab Component
const BankAccountsTab = ({ getAuthHeader }) => {
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddBank, setShowAddBank] = useState(false);
  const [showEditBank, setShowEditBank] = useState(false);
  const [showStatement, setShowStatement] = useState(false);
  const [selectedBank, setSelectedBank] = useState(null);
  const [statement, setStatement] = useState(null);
  const [bankForm, setBankForm] = useState({
    bank_name: '',
    account_number: '',
    ifsc_code: '',
    branch_name: '',
    account_holder_name: '',
    opening_balance: 0,
    is_default: false
  });
  const [editForm, setEditForm] = useState({
    bank_name: '',
    account_number: '',
    ifsc_code: '',
    branch_name: ''
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const fetchBankAccounts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/accounting/bank-accounts`, {
        headers: getAuthHeader()
      });
      setBankAccounts(res.data.filter(a => a.type === 'bank'));
    } catch (error) {
      toast.error('Failed to fetch bank accounts');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchBankAccounts();
  }, []);

  const handleAddBank = async () => {
    if (!bankForm.bank_name || !bankForm.account_number) {
      toast.error('Bank name and account number are required');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/accounting/bank-accounts`, bankForm, {
        headers: getAuthHeader()
      });
      toast.success('Bank account added');
      setShowAddBank(false);
      setBankForm({
        bank_name: '',
        account_number: '',
        ifsc_code: '',
        branch_name: '',
        account_holder_name: '',
        opening_balance: 0,
        is_default: false
      });
      fetchBankAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add bank account');
    }
  };

  const fetchStatement = async (account) => {
    setSelectedBank(account);
    try {
      const res = await axios.get(`${API_URL}/api/accounting/bank-accounts/${account.id}/statement`, {
        headers: getAuthHeader()
      });
      setStatement(res.data);
      setShowStatement(true);
    } catch (error) {
      toast.error('Failed to fetch statement');
    }
  };

  const setAsDefault = async (accountId) => {
    try {
      await axios.put(`${API_URL}/api/accounting/bank-accounts/${accountId}`, 
        { is_default: true },
        { headers: getAuthHeader() }
      );
      toast.success('Default bank account updated');
      fetchBankAccounts();
    } catch (error) {
      toast.error('Failed to update default');
    }
  };

  const openEditDialog = (bank) => {
    setSelectedBank(bank);
    setEditForm({
      bank_name: bank.bank_name || '',
      account_number: bank.account_number || '',
      ifsc_code: bank.ifsc_code || '',
      branch_name: bank.branch_name || ''
    });
    setShowEditBank(true);
  };

  const handleEditBank = async () => {
    if (!selectedBank) return;
    
    try {
      if (selectedBank.account_code === '1100') {
        await axios.put(`${API_URL}/api/accounting/bank-accounts/main`, editForm, {
          headers: getAuthHeader()
        });
      } else {
        await axios.put(`${API_URL}/api/accounting/bank-accounts/${selectedBank.id}`, editForm, {
          headers: getAuthHeader()
        });
      }
      toast.success('Bank account updated');
      setShowEditBank(false);
      fetchBankAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update bank account');
    }
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Bank Accounts</CardTitle>
            <CardDescription>Manage your school's bank accounts</CardDescription>
          </div>
          <Button onClick={() => setShowAddBank(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Bank Account
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : bankAccounts.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No bank accounts configured. Add your first bank account.
            </div>
          ) : (
            <div className="space-y-4">
              {bankAccounts.map((bank) => (
                <div key={bank.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-blue-100 rounded-full">
                      <Building2 className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold">{bank.bank_name || bank.account_name}</h3>
                        {bank.is_default && <Badge variant="secondary">Default</Badge>}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        A/C: ****{bank.account_number?.slice(-4) || '****'} 
                        {bank.ifsc_code && ` | IFSC: ${bank.ifsc_code}`}
                        {bank.branch_name && ` | ${bank.branch_name}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Current Balance</p>
                      <p className={`text-xl font-bold ${bank.current_balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(bank.current_balance)}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => openEditDialog(bank)}>
                        Edit
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => fetchStatement(bank)}>
                        <FileText className="h-4 w-4 mr-1" /> Statement
                      </Button>
                      {!bank.is_default && (
                        <Button variant="ghost" size="sm" onClick={() => setAsDefault(bank.id)}>
                          Set Default
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Bank Account Dialog */}
      <Dialog open={showEditBank} onOpenChange={setShowEditBank}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Bank Account</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Bank Name</Label>
              <Input
                value={editForm.bank_name}
                onChange={(e) => setEditForm({ ...editForm, bank_name: e.target.value })}
                placeholder="e.g., State Bank of India"
              />
            </div>
            <div>
              <Label>Account Number</Label>
              <Input
                value={editForm.account_number}
                onChange={(e) => setEditForm({ ...editForm, account_number: e.target.value })}
                placeholder="Enter account number"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>IFSC Code</Label>
                <Input
                  value={editForm.ifsc_code}
                  onChange={(e) => setEditForm({ ...editForm, ifsc_code: e.target.value })}
                  placeholder="SBIN0001234"
                />
              </div>
              <div>
                <Label>Branch Name</Label>
                <Input
                  value={editForm.branch_name}
                  onChange={(e) => setEditForm({ ...editForm, branch_name: e.target.value })}
                  placeholder="Main Branch"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditBank(false)}>Cancel</Button>
            <Button onClick={handleEditBank}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Bank Account Dialog */}
      <Dialog open={showAddBank} onOpenChange={setShowAddBank}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Bank Account</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Bank Name *</Label>
              <Input
                value={bankForm.bank_name}
                onChange={(e) => setBankForm({ ...bankForm, bank_name: e.target.value })}
                placeholder="e.g., State Bank of India"
              />
            </div>
            <div>
              <Label>Account Number *</Label>
              <Input
                value={bankForm.account_number}
                onChange={(e) => setBankForm({ ...bankForm, account_number: e.target.value })}
                placeholder="Enter account number"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>IFSC Code</Label>
                <Input
                  value={bankForm.ifsc_code}
                  onChange={(e) => setBankForm({ ...bankForm, ifsc_code: e.target.value })}
                  placeholder="SBIN0001234"
                />
              </div>
              <div>
                <Label>Branch Name</Label>
                <Input
                  value={bankForm.branch_name}
                  onChange={(e) => setBankForm({ ...bankForm, branch_name: e.target.value })}
                  placeholder="Main Branch"
                />
              </div>
            </div>
            <div>
              <Label>Account Holder Name</Label>
              <Input
                value={bankForm.account_holder_name}
                onChange={(e) => setBankForm({ ...bankForm, account_holder_name: e.target.value })}
                placeholder="School/Trust Name"
              />
            </div>
            <div>
              <Label>Opening Balance</Label>
              <Input
                type="number"
                value={bankForm.opening_balance}
                onChange={(e) => setBankForm({ ...bankForm, opening_balance: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_default"
                checked={bankForm.is_default}
                onChange={(e) => setBankForm({ ...bankForm, is_default: e.target.checked })}
                className="w-4 h-4"
              />
              <Label htmlFor="is_default">Set as default bank account</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddBank(false)}>Cancel</Button>
            <Button onClick={handleAddBank}>Add Bank Account</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bank Statement Dialog */}
      <Dialog open={showStatement} onOpenChange={setShowStatement}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Bank Statement - {selectedBank?.bank_name || selectedBank?.account_name}
            </DialogTitle>
          </DialogHeader>
          {statement && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4 p-4 bg-muted rounded-lg">
                <div>
                  <p className="text-sm text-muted-foreground">Opening Balance</p>
                  <p className="text-lg font-bold">{formatCurrency(statement.opening_balance)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Debits</p>
                  <p className="text-lg font-bold text-green-600">{formatCurrency(statement.summary?.total_debits)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Credits</p>
                  <p className="text-lg font-bold text-red-600">{formatCurrency(statement.summary?.total_credits)}</p>
                </div>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Voucher No</TableHead>
                    <TableHead>Narration</TableHead>
                    <TableHead className="text-right">Debit</TableHead>
                    <TableHead className="text-right">Credit</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {statement.statement?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground">
                        No transactions found
                      </TableCell>
                    </TableRow>
                  ) : (
                    statement.statement?.map((entry, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{entry.date}</TableCell>
                        <TableCell className="font-mono text-sm">{entry.voucher_no}</TableCell>
                        <TableCell>{entry.narration}</TableCell>
                        <TableCell className="text-right text-green-600">
                          {entry.debit > 0 ? formatCurrency(entry.debit) : '-'}
                        </TableCell>
                        <TableCell className="text-right text-red-600">
                          {entry.credit > 0 ? formatCurrency(entry.credit) : '-'}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(entry.balance)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AccountingPage;
